/**
 * THE BRAND MARKS — one resolver for every place the product shows its identity.
 *
 * ── WHAT THE MARK IS ────────────────────────────────────────────────────────
 * The icon is the riftgate itself: two stone towers with the rift burning
 * between them. Every mark is derived by `tools/assetgen/scripts/ingest-brand.mjs`
 * from one master, `assets/incoming/brand/logo-riftgate.png`, and lives in
 * `public/brand/`, so each is a FILE THE BROWSER FETCHES rather than a `data:`
 * URI in the bundle — the meta shell has a 150 KB gz budget (ADR-0005), and the
 * icon alone is 34 KB, so inlining the set would spend a fifth of the whole
 * allowance on identity.
 *
 * The master CHANGED once, and the reason is worth keeping: `emblem-gate.png`
 * is a painterly illustration on a painted ground whose stone sits at nearly
 * the value of the backdrop behind it. It reads at 512 px and collapses at 32,
 * where it becomes a grey blob with an orange stripe. A logo is a silhouette,
 * not a small illustration, so the master is drawn as one. `emblem-gate.png` is
 * kept for hero and splash use, where it is good.
 *
 * ── WHY THE WORDMARK IS TEXT AND NOT ART ────────────────────────────────────
 * STYLE_BIBLE §7 bans baked text, numerals and logos in art. A logo is the one
 * honest exception to that rule — it is a mark, not a label — but the exception
 * is only worth taking where a single flat file is genuinely required. On the
 * web it is not:
 *
 *   • `brandLockup()` sets the name as REAL TEXT beside the icon. It stays
 *     crisp at any zoom and on any DPR, it is selectable and searchable, a
 *     screen reader reads it, and it is the runtime brand name — which in a
 *     preview build the server has already rewritten (see `brandName()` in
 *     `site-info.ts`). A baked wordmark could do none of those things.
 *   • `BRAND_MARKS.og` / `BRAND_MARKS.ogWordmark` ARE baked, because an Open
 *     Graph card is a single flat PNG handed to a crawler that runs no CSS and
 *     no JS. There is no way to set live text inside one. Those two files are
 *     the only place in this repo where a wordmark is pixels.
 *
 * The pair answers the ask directly: `og.png` is the lockup WITH the icon,
 * `og-wordmark.png` is the same lockup WITHOUT it.
 *
 * ── WHY A MODULE AND NOT A STRING IN EACH SCREEN ────────────────────────────
 * Paths hardcoded per screen are how a repo ends up with one screen pointing at
 * a file that was renamed. Every consumer — the header, `index.html`'s favicon
 * links, any future screen — resolves through `BRAND_MARKS`, and
 * `test/brand.test.ts` asserts each entry is a real file on disk.
 */

import { el, type Attrs } from './dom';

/** Where the marks are served from. Mirrors the directory in `public/`. */
export const BRAND_ASSET_PREFIX = '/brand/';

/**
 * Every shipped mark, keyed by the job it does. The sizes are not decorative:
 * a browser tab draws 16–32 CSS px, a header slot here is 32 px (so 96 covers
 * a 3× phone without a second request), Apple asks for exactly 180, and the
 * Open Graph card is the 1.91:1 / 1200×630 both Facebook and X document.
 */
export const BRAND_MARKS = {
  /**
   * The mark alone, transparent. ONE file, at 192 px: it is the `src` for every
   * on-screen use, and 192 covers a 64 px slot on a 3× phone with headroom. A
   * 512 px master was cut because nothing rendered at that size — a 330 KB PNG
   * in `public/` that no consumer names is the "shipped and reachable from
   * nothing" defect this repo keeps finding. Regenerate a larger export from
   * `assets/incoming/brand/emblem-gate.png` if a use ever appears.
   */
  icon: `${BRAND_ASSET_PREFIX}icon-192.png`,
  /** The browser tab. */
  favicon: `${BRAND_ASSET_PREFIX}favicon-32.png`,
  /**
   * iOS home screen. OPAQUE on the §8 ground `#151A21`, unlike every other
   * mark here, because iOS composites an alpha touch icon onto black and the
   * gate's own dark outline would vanish into it.
   */
  appleTouchIcon: `${BRAND_ASSET_PREFIX}apple-touch-icon.png`,
  /** Share card, lockup WITH the icon. Wordmark baked — see the note above. */
  og: `${BRAND_ASSET_PREFIX}og.png`,
  /** Share card, wordmark alone, WITHOUT the icon. */
  ogWordmark: `${BRAND_ASSET_PREFIX}og-wordmark.png`,
  /**
   * THE FULL LOCKUP — gate icon plus the "RIFTGATE ARENA" wordmark, one flat
   * transparent PNG (640×361). This is the owner's generated logo.
   *
   * ── WHY THIS ONE IS BAKED WHILE THE HEADER STAYS LIVE TEXT ──────────────
   * The header pairs `icon` with the name as REAL TEXT (`brandLockup()`): it
   * has to stay crisp in a 32 px slot, translatable, selectable, and it must
   * show the name a preview build rewrote. A wide wordmark PNG crammed into a
   * 32 px header slot is unreadable, so the header never uses this file.
   *
   * The MARKETING hero is the opposite case, and the one honest place for a
   * baked wordmark: it is the single most visible statement of the product's
   * identity, it may stand alone, and a flat PNG renders identically to a
   * crawler that runs no CSS — the same reason the OG cards are baked. So the
   * hero uses this lockup and gives it a real accessible name; the header does
   * not touch it.
   */
  lockup: `${BRAND_ASSET_PREFIX}lockup.png`,
} as const;

/** The lockup's intrinsic pixel size (trimmed tight to the artwork — the
 * source PNG carried ~60% transparent vertical padding), so callers reserve the
 * right box and the logo fills its height instead of floating in empty space. */
export const BRAND_LOCKUP_SIZE = { width: 493, height: 139 } as const;

export type BrandMark = keyof typeof BRAND_MARKS;

/** How a mark is announced. See `brandIcon()`. */
export interface BrandIconOptions {
  /** Rendered box, CSS px. Both dimensions — the mark is square. */
  readonly size?: number;
  /**
   * The accessible name, when the icon is the ONLY identity in its context.
   * OMIT IT when a text brand name sits beside the icon: the icon is then
   * decorative, and naming it too makes a screen reader announce the product
   * twice in a row. `brandLockup()` relies on exactly that.
   */
  readonly label?: string;
  /** Extra classes for the `<img>`. */
  readonly class?: string;
}

/**
 * The mark on its own.
 *
 * `width`/`height` are always set so the box is reserved before the PNG
 * arrives and the header does not jump when it does. `decoding="async"`
 * keeps the decode off the first-paint path.
 */
export function brandIcon(options: BrandIconOptions = {}): HTMLImageElement {
  const size = options.size ?? 32;
  const named = typeof options.label === 'string' && options.label.length > 0;
  const attrs: Attrs = {
    src: BRAND_MARKS.icon,
    width: size,
    height: size,
    decoding: 'async',
    class: options.class ?? '',
    // Decorative: EMPTY alt, not a missing one. A missing `alt` makes a screen
    // reader fall back to reading the file name out loud.
    alt: named ? (options.label as string) : '',
    'aria-hidden': named ? false : 'true',
  };
  return el('img', attrs) as HTMLImageElement;
}

/** How the lockup is rendered. See `brandLockup()`. */
export interface BrandLockupOptions {
  /** The brand name. Callers pass `brandName(siteInfo)` — this module does not fetch. */
  readonly name: string;
  /** Icon box, CSS px. Default 32. */
  readonly size?: number;
  /** Classes for the wordmark `<span>`. */
  readonly textClass?: string;
  /** Defaults to `icon-left`. See {@link BrandLockupDirection}. */
  readonly direction?: BrandLockupDirection;
}

/**
 * THE LOCKUP: the icon, then the name as live text.
 *
 * Returns the two children rather than a wrapper, so the caller keeps control
 * of the element they hang on (the site header puts them straight into its
 * existing `<a href="/">`, which already has the tap target and the focus
 * ring). Wrapping them here would have meant a nested anchor or a second box
 * to style.
 *
 * The icon is decorative BY CONSTRUCTION: the name is right there as text, so
 * `brandIcon()` is called without a `label`.
 */
/**
 * Which side of the name the mark sits on, or whether it stacks.
 *
 * The owner asked for "icon on the left and text, icon on the right and the
 * text and different directions". These are DOM arrangements of one icon file
 * and live text, NOT four baked images: the wordmark stays selectable,
 * translatable and crisp at any DPR, and a preview build still shows the name
 * the server rewrote (`brandName()` in `site-info.ts`). Baking four lockups
 * would freeze all of that into pixels, and STYLE_BIBLE §7 keeps letters out of
 * art for exactly that reason.
 *
 * `icon-only` is here so a caller that wants the bare mark goes through this
 * function and gets the accessible name handled, rather than reaching for
 * `brandIcon` and forgetting it.
 */
export type BrandLockupDirection = 'icon-left' | 'icon-right' | 'icon-above' | 'icon-only';

/**
 * The mark and the name, arranged.
 *
 * ORDER IS VISUAL, NOT SEMANTIC. `icon-right` returns the text node FIRST and
 * the icon second, so the reading order a screen reader and a keyboard follow
 * matches what the eye sees — flipping the visuals with `flex-direction` while
 * leaving the DOM order alone is the classic way to make a lockup read
 * backwards to everyone not using their eyes.
 *
 * The caller owns the container and therefore the axis: `icon-above` returns
 * the same two nodes and expects a column. The returned nodes carry no layout
 * of their own beyond `shrink-0` on the mark, because a lockup that positioned
 * itself could not be dropped into a header, a card and a splash alike.
 */
export function brandLockup(options: BrandLockupOptions): readonly Node[] {
  const direction = options.direction ?? 'icon-left';
  const size = options.size ?? 32;
  // The icon is decorative WHENEVER the name is beside it — the text is already
  // the accessible name, and a second one just makes a screen reader say it
  // twice. Alone, it has to carry the name itself or it is an unlabelled image.
  const icon =
    direction === 'icon-only'
      ? brandIcon({ size, label: options.name, class: 'shrink-0' })
      : brandIcon({ size, class: 'shrink-0' });
  if (direction === 'icon-only') return [icon];

  const text = el('span', { class: options.textClass ?? '', text: options.name });
  return direction === 'icon-right' ? [text, icon] : [icon, text];
}

/** How the marketing hero lockup is rendered. See {@link brandHeroLockup}. */
export interface BrandHeroLockupOptions {
  /**
   * The accessible name. The lockup is baked pixels of the wordmark, so this
   * describes what the image shows — the product identity. Defaults to the
   * product name; a preview build cannot rewrite a flat PNG, so unlike the
   * live-text header lockup this is a fixed string, not a runtime value.
   */
  readonly name?: string;
  /** Classes for the `<img>`. The caller sizes it — jsdom cannot judge size. */
  readonly class?: string;
}

/**
 * THE FULL LOCKUP AS ONE FLAT IMAGE, for the marketing / landing hero.
 *
 * A NAMED `<img>` (not decorative): here the lockup may be the only statement
 * of the product's identity on the page, so it carries a real accessible name
 * rather than the empty `alt` the header icon gets beside its live text.
 *
 * `width`/`height` are the file's intrinsic pixels so the box is reserved
 * before the PNG lands; the caller controls the rendered size with CSS. What a
 * browser must confirm and jsdom cannot: that the chosen size reads on both
 * mobile and desktop and that the transparent wordmark holds on the §8 ground.
 */
export function brandHeroLockup(options: BrandHeroLockupOptions = {}): HTMLImageElement {
  return el('img', {
    src: BRAND_MARKS.lockup,
    width: BRAND_LOCKUP_SIZE.width,
    height: BRAND_LOCKUP_SIZE.height,
    decoding: 'async',
    alt: options.name ?? 'Riftgate Arena',
    class: options.class ?? '',
  }) as HTMLImageElement;
}
